// ============================================================================
// ROLE PERMISSION SERVICE
// ============================================================================
// Backend wraps responses in ApiResponse<T>. List endpoints wrap data in
// PagedResult<T> (items, page, pageSize, totalCount, totalPages, hasPrevious, hasNext).
// getAll() transforms this into the frontend PaginatedResponse<T> shape.

import { get, post, put, del } from '../client/axiosClient';
import { rolePermissionEndpoints } from '../endpoints/rolePermission.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
} from '../types';
import type {
  RolePermission,
  CreateRolePermissionRequest,
  UpdateRolePermissionRequest,
  RolePermissionMerged,
  RolePermissionFilterParams,
} from '../types/rolePermission.types';

class RolePermissionService {
  /**
   * Get paginated list with optional filters.
   * Transforms backend PagedResult<T> → frontend PaginatedResponse<T>.
   */
  async getAll(
    params?: RolePermissionFilterParams
  ): Promise<PaginatedResponse<RolePermission>> {
    const endpoint = params
      ? buildEndpointWithQuery(rolePermissionEndpoints.basePath(), params as Record<string, any>)
      : rolePermissionEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw?.data ?? {};

    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 25,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<RolePermission>> {
    const response = await get<ApiResponse<RolePermission>>(
      rolePermissionEndpoints.getById(id)
    );
    return response.data;
  }

  /**
   * Fetch merged permissions for a comma-separated list of role codes.
   * Called at login time to determine visible modules and allowed actions.
   * Also triggers UserCacheMiddleware on the backend (registers user in tbl_user_cache).
   */
  async getByRoles(roles: string): Promise<ApiResponse<RolePermissionMerged>> {
    const endpoint = buildEndpointWithQuery(rolePermissionEndpoints.byRoles(), { roles });
    const response = await get<ApiResponse<RolePermissionMerged>>(endpoint);
    return response.data;
  }

  async create(
    payload: CreateRolePermissionRequest
  ): Promise<ApiResponse<RolePermission>> {
    const response = await post<ApiResponse<RolePermission>>(
      rolePermissionEndpoints.create(),
      payload
    );
    return response.data;
  }

  async update(
    id: ID,
    payload: UpdateRolePermissionRequest
  ): Promise<ApiResponse<RolePermission>> {
    const response = await put<ApiResponse<RolePermission>>(
      rolePermissionEndpoints.update(id),
      payload
    );
    return response.data;
  }

  async delete(id: ID): Promise<EmptyResponse> {
    await del<void>(rolePermissionEndpoints.delete(id));
    return { success: true };
  }
}

export const rolePermissionService = new RolePermissionService();
export default rolePermissionService;
